import {createAction, props} from '@ngrx/store';
import {IStore} from '../../../shared/shared-models/store/store';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {IUser} from '../../user-access/models';
import {IPriceBand} from 'src/app/shared/shared-models/price-banding/price-band';
import {
  IDisableStockItemRule,
  IEmailSettings,
  IEnableStockItemRule,
  IOnHandDisableRule,
  IOpenOrderSettings,
  IStockItemEditThresholds,
  ITableNavSettings,
  TLineColour,
  TLocaleDateFormat,
} from '../../settings-new/models';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';

//#region All User and Store Settings

export const getAllUserSettings = createAction(
  '[User Settings][User] Get All User Settings',
);

export const getAllUserStoreSettings = createAction(
  '[User Settings][User Store] Get All User Store Settings',
);

export const updateAllUserSettings = createAction(
  '[User Settings][Update User Settings] Update All User Settings',
  props<{
    fitToGrid: boolean;
    ngpPreviewColumns: Record<number, string>;
    stockPreviewColumns: Record<number, string>;
    tableNavSettings: ITableNavSettings;
    openOrderSettings?: IOpenOrderSettings;
  }>(),
);

export const updateAllUserSettingsSuccess = createAction(
  '[User Settings][Update User Settings] Update All User Settings Success',
  props<{
    fitToGrid: boolean;
    ngpPreviewColumns: Record<number, string>;
    stockPreviewColumns: Record<number, string>;
    tableNavSettings: ITableNavSettings;
    openOrderSettings?: IOpenOrderSettings;
  }>(),
);

export const updateAllUserSettingsFailure = createAction(
  '[User Settings][Update User Settings] Update All User Settings Failure',
  props<{
    error: IGlobalError
  }>(),
);

//#endregion

//#region Table Settings

export const getTableSettings = createAction(
  '[User Settings][Table Settings] Get Table Settings',
);

export const getTableSettingsSuccess = createAction(
  '[User Settings][Table Settings] Get Table Settings Success',
  props<{ tableNavSettings: ITableNavSettings, fitToGrid: boolean, store: IStore }>(),
);

export const getTableSettingsFailure = createAction(
  '[User Settings][Table Nav] Get Table Settings Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

export const setTableNavSettings = createAction(
  '[User Settings][Table Nav] Set Table Nav Settings',
  props<{ settings: ITableNavSettings }>(),
);

export const setTableNavSettingsSuccess = createAction(
  '[User Settings][Table Nav] Set Table Nav Settings Success',
  props<{ settings: ITableNavSettings, store: IStore }>(),
);

export const setTableNavSettingsFailure = createAction(
  '[User Settings][Table Nav] Set Table Nav Settings Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

export const setFitToGrid = createAction(
  '[Shared Grid][Grid Layout] Set Fit To Grid',
  props<{ fitToGrid: boolean | null }>(),
);

export const toggleFitToGrid = createAction(
  '[Shared Grid][Grid Layout] toggle Fit To Grid',
);

export const setFitToGridSuccess = createAction(
  '[FitToGrid] Set Fit To Grid Success',
  props<{ fitToGrid: boolean }>(),
);

export const setFitToGridFailure = createAction(
  '[FitToGrid] Set Fit To Grid Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Item Disabling and Enabling Rules

export const getItemDisablingRules = createAction(
  '[Store Settings][Item Disabling Rules] Get Item Disabling Rules',
);

export const getItemDisablingRulesSuccess = createAction(
  '[Store Settings][Item Disabling Rules] Get Item Disabling Rules Success',
  props<{ rulesDoc: IDisableStockItemRule, store: IStore }>(),
);

export const getItemDisablingRulesFailure = createAction(
  '[Store Settings][Item Disabling Rules] Get Item Disabling Rules Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

export const getItemEnablingRulesSuccess = createAction(
  '[Store Settings][Item Disabling Rules] Get Item Enabling Rules Success',
  props<{ rulesDoc: IEnableStockItemRule, store: IStore }>(),
);

export const getItemEnablingRulesFailure = createAction(
  '[Store Settings][Item Disabling Rules] Get Item Enabling Rules Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

export const updateOnHandSettings = createAction(
  '[User Store Settings][Update Store Settings] Update On Hand Settings',
  props<{
    ruleType: string;
    onHandZero: IOnHandDisableRule;
    onHandNotZero: IOnHandDisableRule;
  }>(),
);

export const updateEnablingSettings = createAction(
  '[User Store Settings][Update Store Settings] Update Enabling Settings',
  props<{ formData: IEnableStockItemRule }>(),
);

export const updateOnHandSettingsSuccess = createAction(
  '[User Store Settings][Update Store Settings] Update On Hand Settings Success',
);

export const updateOnHandSettingsFailure = createAction(
  '[User Store Settings][Update Store Settings] Update On Hand Settings Failure',
  props<{
    error: IGlobalError
  }>(),
);

//#endregion

//#region Line Colour

export const getLineColour = createAction(
  '[Store Settings][Line Colour] Get Line Colour',
);

export const getLineColourSuccess = createAction(
  '[Store Settings][Line Colour] Get Line Colour Success',
  props<{ colour: TLineColour, store: IStore }>(),
);

export const getLineColourFailure = createAction(
  '[Store Settings][Line Colour] Get Line Colour Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

//#endregion

//#region Stock Edit Thresholds

export const getStockEditThresholds = createAction(
  '[User Settings][Stock Edit Thresholds] Get Stock Edit Thresholds',
);

export const getStockEditThresholdsSuccess = createAction(
  '[User Settings][Stock Edit Thresholds] Get Stock Edit Thresholds Success',
  props<{
    thresholds: IStockItemEditThresholds,
    storeId: IStore['storeId']
  }>(),
);

export const getStockEditThresholdsFailure = createAction(
  '[User Settings][Stock Edit Thresholds] Get Stock Edit Thresholds Failure',
  props<{
    error: IGlobalError
  }>(),
);

export const updateStockEditThresholds = createAction(
  '[User Store Settings][Update Stock Threshold] Update Stock Edit Thresholds',
  props<{
    thresholds: IStockItemEditThresholds
  }>(),
);

export const updateStockEditThresholdsSuccess = createAction(
  '[User Store Settings][Update Stock Threshold] Update Stock Edit Thresholds Success',
);

export const updateStockEditThresholdsFailure = createAction(
  '[User Store Settings][Update Stock Threshold] Update Stock Edit Thresholds Failure',
  props<{
    error: IGlobalError
  }>(),
);

//#endregion

//#region Price Bands

export const getPriceBands = createAction(
  '[User Settings][Price Banding] Get Price Banding',
);

export const getPriceBandsSuccess = createAction(
  '[User Settings][Price Banding] Get Price Banding Success',
  props<{
    priceBands: Record<number, IPriceBand>,
    storeId: IStore['storeId']
  }>(),
);

export const getPriceBandsFailure = createAction(
  '[User Settings][Price Banding] Get Price Banding Failure',
  props<{
    error: IGlobalError
  }>(),
);

//#endregion

//#region Preview Columns - Stock Manager

export const getStockManagerColPreviewSettings = createAction(
  '[User Settings][Stock Manager] Get Stock Manager Column Preview Settings',
);

export const getStockManagerColPreviewSettingsSuccess = createAction(
  '[User Settings][Stock Manager] Get Stock Manager Column Preview Settings Success',
  props<{ colPreview: Record<number, string>, store: IStore }>(),
);

export const getStockManagerColPreviewSettingsFailure = createAction(
  '[User Settings][Stock Manager] Get Stock Manager Column Preview Settings Failure',
  props<{ error: IGlobalError }>(),
);

export const setStockManagerPreviewColumns = createAction(
  '[User Settings][Stock Manager] Set Stock Manager Preview Columns',
  props<{ columnData: {} }>(),
);

export const setStockManagerPreviewColumnsSuccess = createAction(
  '[User Settings][Stock Manager] Set Stock Manager Preview Columns Success',
  props<{ columnData: {}; store: IStore }>(),
);

export const setStockManagerPreviewColumnsFailure = createAction(
  '[User Settings][Stock Manager] Set Stock Manager Preview Columns Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

//#endregion

//#region Preview Columns - NGP Report

export const getNGPReportColPreviewSettings = createAction(
  '[User Settings][NGP Report] Get NGP Report Column Preview Settings',
);

export const getNGPReportColPreviewSuccess = createAction(
  '[User Settings][NGP Report] Get NGP Report Column Preview Success',
  props<{ store: IStore, colPreview: Record<number, string> }>(),
);

export const getNGPReportColPreviewFailure = createAction(
  '[User Settings] Get NGP Report Column Preview Failure',
  props<{ errors: IGlobalError[]; store: IStore }>(),
);

export const setNGPReportColPreviewSettings = createAction(
  '[User Settings][NGP Report] Set NGP Report Column Preview Settings',
  props<{ columnData: {} }>(),
);

export const setNGPReportColPreviewSettingsSuccess = createAction(
  '[User Settings][NGP Report] Set NGP Report Column Preview Settings Success',
  props<{ columnData: {}; store: IStore }>(),
);

export const setNGPReportColPreviewSettingsFailure = createAction(
  '[User Settings][NGP Report] Set NGP Report Column Preview Settings Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

//#endregion

//#region Email Settings

export const getUserEmailSettings = createAction(
  '[User Settings][Email Details] Get  Email Settings',
);

export const getUserEmailSettingsSuccess = createAction(
  '[User Settings][Email Details] Get User Email Settings Success',
  props<{ settings: IEmailSettings, store: IStore, user: IUser }>(),
);

export const getUserEmailSettingsFailure = createAction(
  '[User Settings][Email Details] Get User Email Settings Failure',
  props<{ error: IGlobalError }>(),
);

export const getAllUserEmailSettings = createAction(
  '[User Settings][Email Details] Get All Users Email Settings',
);

export const getAllUserEmailSettingsSuccess = createAction(
  '[User Settings][Email Details] Get All Users Email Settings Success',
  props<{ usersEmailSettings: Record<string, IEmailSettings>; store: IStore }>(),
);

export const getAllUserEmailSettingsFailure = createAction(
  '[User Settings][Email Details] Get All Users Email Settings Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * TODO TECHNICAL DEBT - Get Supplier Emails
 * This will be for future implementation on Auto Orders
 */
export const getSupplierEmails = createAction(
  '[User Settings][Supplier Emails] Get Supplier Emails',
  props<{ supplierIds: string[] }>(),
);

export const getSupplierEmailsSuccess = createAction(
  '[User Settings][Supplier Emails] Get Supplier Emails Success',
  props<{ supplierEmails: Record<string, string[]>; store: IStore }>(),
);

export const getSupplierEmailsFailure = createAction(
  '[User Settings][Supplier Emails] Get Supplier Emails Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

export const testEmail = createAction(
  '[Store Settings][Test Email] Test Email',
  props<{ storeId: string; userId: string; }>(),
);

export const testEmailSuccess = createAction(
  '[Store Settings][Test Email] Test Email Success',
  props<{ status: string }>(),
);

export const testEmailFailure = createAction(
  '[Store Settings][Test Email] Test Email Failure',
  props<{ error: IGlobalError }>(),
);

export const showModal = createAction(
  '[Store Settings][Test Email] Show Modal',
  props<{ modalProps: ISharedModalBasic }>(),
);

//#endregion


//#region Overrides

export const getOverrides = createAction(
  `[STORE SETTINGS][Over Rides] Get Overrides`,
  props<{ store: IStore }>(),
);

export const getOverridesSuccess = createAction(
  `[STORE SETTINGS][Open Order] Get Overrides Success`,
  props<{ overrides: { orderBlankSupplierCode: boolean; shelfTalkersEnabled: boolean }; store: IStore }>(),
);

export const setSupplierOverride = createAction(
  `[STORE SETTINGS][Open Order] Set Supplier Override`,
  props<{ supplierOverRide: boolean }>(),
);

export const setSupplierOverrideSuccess = createAction(
  `[STORE SETTINGS][Open Order] Set Supplier Override Success`,
  props<{ supplierOverRide: boolean; store: IStore }>(),
);

export const setShelfTalkerOverride = createAction(
  `[STORE SETTINGS][Over Rides] Set ShelfTalker Override`,
  props<{ shelfTalkerOverRide: boolean }>(),
);

export const setShelfTalkerOverrideSuccess = createAction(
  `[STORE SETTINGS][Open Order] Set ShelfTalker Override Success`,
  props<{ shelfTalkerOverRide: boolean; store: IStore }>(),
);

//#endregion

//#region Open Order Settings

export const getOpenOrderSettings = createAction(
  '[User Settings][Open Order] Get Open Order Settings',
);

export const getOpenOrderSettingsSuccess = createAction(
  '[User Settings][Open Order] Get Open Order Settings Success',
  props<{ openOrderSettings: IOpenOrderSettings; store: IStore }>(),
);

export const getOpenOrderSettingsFailure = createAction(
  '[User Settings][Open Order] Get Open Order Settings Failure',
  props<{ error: IGlobalError; store: IStore }>(),
);

export const updateOpenOrderSettings = createAction(
  '[User Settings][Open Order] update Open Order Settings',
  props<{ openOrderSettings: IOpenOrderSettings; }>(),
);

export const updateOpenOrderSettingsSuccess = createAction(
  '[User Settings][Open Order] update Open Order Settings Success',
  props<{ openOrderSettings: IOpenOrderSettings, store: IStore }>(),
);

export const updateOpenOrderSettingsFailure = createAction(
  '[User Settings][Open Order] update Open Order Settings Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Add Stock Item Rules

export const getAddStockItemRules = createAction(
  `[STORE SETTINGS][Add Stock Item] Get Add Stock Item Rules`,
);

export const getAddStockItemRulesSuccess = createAction(
  `[STORE SETTINGS][Add Stock Item] Get Add Stock Item Rules Success`,
  props<{ store: IStore, suffix: string; prefix: string; digits: number }>(),
);

export const setAddStockItemRules = createAction(
  `[STORE SETTINGS][Add Stock Item] Set Add Stock Item Rules`,
  props<{ suffix: string; prefix: string; digits: number }>(),
);

export const setAddStockItemRulesSuccess = createAction(
  `[STORE SETTINGS][Add Stock Item] Set Add Stock Item Rules Success`,
  props<{ storeId: string, suffix: string; prefix: string; digits: number }>(),
);

//#endregion


//#region Store Maintenance

export const getIsStoreUnderMaintenance = createAction(
  '[Store Settings][Maintenance] Get Is Store Under Maintenance',
);

//#endregion
